<template>
    <div>
        <div>
            <div class="header">
                <h2>Your current plan</h2>
            </div>
            <div class="plan--current white">
                <div class="plan">
                    <template v-if="account.plan">
                        <div class="heading">
                            <h3>{{ account.plan.display }} Plan</h3>
                            <div class="price">
                                <span>$</span>
                                <strong>{{ account.plan.price }}</strong>
                                <small>/<template v-if="account.plan.yearly">year</template><template v-else>month</template></small>
                            </div>
                            <router-link :to="{ name: 'upgrade' }" title="Click here to upgrade your account">
                                Change your plan
                            </router-link>
                        </div>
                        <ul>
                            <li>{{ account.plan.credits|number }} credits per month</li>
                        </ul>
                        <div class="downgrade">
                            <a href="javascript:;" title="Click here to cancel your subscription" @click="showDowngradeModal" v-if="account.subscription_ends_at === null" :class="{'btn-disabled': processing}" :disabled="processing">Cancel your subscription</a>
                            <p v-else>
                                Your subscription will end <time :datetime="account.subscription_ends_at|isoformat" :data-tooltip="account.subscription_ends_at|datetime('MMMM DD, YYYY')">{{ account.subscription_ends_at|relative }}</time>.
                                <a href="javascript:;" title="Re-enable the subscription" class="btn" @click="enableSubscription()" :class="{'btn-disabled': processing}" :disabled="processing" v-if="cards && cards.length > 0">Re-enable it</a>
                            </p>
                        </div>
                    </template>
                    <template v-else>
                        <div class="heading">
                            <h3>Free plan</h3>
                            <router-link :to="{ name: 'upgrade' }" title="Click here to upgrade your account">
                                Upgrade
                            </router-link>
                        </div>
                        <ul>
                            <li>Limited to 1Mb per documents</li>
                            <li>50 credits per month</li>
                        </ul>
                    </template>
                </div>
            </div>
        </div>
        <div class="cards">
            <div class="header">
                <h2>Payment methods</h2>
            </div>
            <div class="content">
                <div class="card" v-for="card in cards" :key="card.id" :class="{'primary': card.default}">
                    <div class="card--header">
                        <img :src="`/images/cards/${card.brand_img}.png`" :alt="card.brand" />
                        <a v-if="canRemoveCard(card)" href="javascript:;" title="Remove this card" @click="remove(card)" class="remove" :class="{'disabled': processing}"><svg-remove /></a>
                        <span v-else class="tooltip tooltip-top" data-tooltip="You can't remove this card because you are on an active plan."><a href="javascript:;" title="Remove this card" class="disabled remove"><svg-remove /></a></span>
                    </div>
                    <div class="card--number">
                        <template v-if="card.name">{{ card.name }}<br /></template><template v-else><br /></template>
                        &bull;&bull;&bull;&bull;&nbsp;&nbsp;&bull;&bull;&bull;&bull;&nbsp;&nbsp;&bull;&bull;&bull;&bull;&nbsp;&nbsp;{{ card.last4 }}
                    </div>
                    <div class="card--details">
                        <span class="expires">{{ card.expire.split('/').join(' / ') }}</span>
                        <div>
                            <template v-if="card.default"><em>Default card</em></template>
                            <template v-else>
                                <a href="javascript:;" title="Set this card as the default" @click="setAsDefault(card.id)" :class="{'disabled': processing}">Make default</a>
                            </template>
                        </div>
                    </div>
                </div>
                <div class="card white card--add" v-if="cards && cards.length < 3">
                    <a href="javascript:;" title="Click here to add another card" @click="showAddModal" class="add-action" :class="{'disabled': processing}">
                        <svg-plus />
                    </a>
                </div>
            </div>
        </div>
        <modal id="modal-stripe-card" ref="modal" title="Add a new credit card" submit="Add a new card" submitClass="btn-full" @submit="processCard">
            <stripe-card ref="stripe" :name="account.name" />
        </modal>
        <modal id="cancel-survey" ref="downgrade" submit="Cancel my subscription" @shown="$refs.reason.focus()" @submit="downgrade" submitClass="btn-danger" closeLabel="Keep my subscription">
            <div class="cancel-survey-content">
                <!--
                <div class="message">
                    <h5>From founder to founder...</h5>
                    <p>I would love to know the reason why you are deciding to stop using PDFShift?</p>
                    <p>This would help me better understand your needs and align more PDFShift with other like-minded people.</p>
                </div>
                <div class="message">
                    <h5>Wanna be the hero of the day?</h5>
                    <p>Sharing the reason why you are leaving will help us improve PDFShift and help others in the same situation as you.</p>
                </div>
                -->
                <div class="message">
                    <h5>From founder to founder</h5>
                    <p>I'm Cyril, founder of PDFShift.</p>
                    <p>It would love to know the reason why you are leaving PDFShift.</p>
                    <p>I'm hoping this would help us, and others like you, by improving the experience around PDFShift.</p>
                </div>
                <div class="form-group">
                    <select v-model="churn.reason">
                        <option value="lack_of_use">I'm not using it</option>
                        <option value="cost">It's too expensive</option>
                        <option value="internal">We built our own solution</option>
                        <option value="features">It lacks key features</option>
                        <option value="experience">I'm having technical issues</option>
                        <option value="alternative">I'm using a different tool</option>
                        <option value="other">Other reason(s)</option>
                    </select>
                </div>

                <div v-if="churn.reason !== 'lack_of_use' && churn.reason !== 'internal'" class="form-group">
                    <textarea ref="reason" v-model="churn.message" rows="4" :placeholder="churnPlaceholder" maxlength="10000"></textarea>
                    <p class="help-text">This message goes directly to my personal inbox. I read every message myself.</p>
                </div>
                <div v-else-if="churn.reason === 'lack_of_use'" class="form-group">
                    <p class="help-text">I'm really sorry PDFShift wasn't useful for you.<br />If there's anything I can do to help, please reach out to me at <a href="mailto:cyril@pdfshift.io" target="_blank">cyril@pdfshift.io</a>.</p>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import SvgPlus from '@/assets/images/plus.svg'
import SvgRemove from '@/assets/images/delete.svg'
import Modal from '@/components/Modal'
import StripeCard from '@/components/StripeCard'

export default {
    components: { SvgRemove, SvgPlus, Modal, StripeCard },
    data () {
        return {
            cards: null,
            churn: {
                reason: 'other',
                message: null
            },
            processing: false
        }
    },
    computed: {
        ...mapState({
            account: state => state
        }),
        churnPlaceholder () {
            switch (this.churn.reason) {
                case 'cost':
                    return 'What do you think would be a fair price?'
                case 'features':
                    return 'Which feature(s) would you expect on PDFShift?'
                case 'alternative':
                    return 'Ohoh! A competitor ?\nI would love to know which one ಠಿ_ಠ'
                case 'experience':
                    return 'Please share your issues, or reach out to support@pdfshift.io and we will help you resolve it'
                case 'other':
                default:
                    return 'A small message would mean the world to us'
            }
        }
    },
    created () {
        this.$http.get('account/cards/').then(
            response => {
                this.cards = response.body.cards
            }
        )
    },
    methods: {
        showAddModal () {
            this.$refs.stripe.reset()
            this.$refs.modal.show()
            this.$nextTick(() => {
                this.$refs.stripe.setFocus()
            })
        },
        async processCard () {
            const token = await this.$refs.stripe.process()
            if (!token) return null

            try {
                const response = await this.$http.post('account/cards/', { token })
                this.$refs.modal.close()
                this.cards.push(response.body.card)
            } catch (e) {
                try {
                    this.$refs.stripe.setError(e.response.body.errors.token[0])
                } catch (ee) {
                    this.$toast.error(e.body?.error || 'An error occured while trying to add your card. Please try again later.')
                }
            }
        },
        async setAsDefault (cardId) {
            if (this.processing) {
                return
            }

            this.processing = true
            try {
                const response = await this.$http.put('account/cards/' + cardId)
                this.cards = response.body.cards
                const card = this.cards.find(x => x.id === cardId)
                this.$toast.info(`Your card ${card.brand} ending with ${card.last4} was successfully set as the default.`)
            } catch (e) {
                let message = 'An error occured when trying to set your card as the default.'
                try {
                    message = e.response.body.error
                } catch (e) {}
                this.$toast.error(message)
            } finally {
                this.processing = false
            }
        },
        async remove (card) {
            if (this.processing) {
                return
            }

            if (!confirm(`Are you sure you want to remove the ${card.brand} card ending with ${card.last4}?`)) {
                return
            }

            this.processing = true

            try {
                await this.$http.delete('account/cards/' + card.id)
                const idx = this.cards.findIndex(x => x.id === card.id)
                this.cards.splice(idx, 1)
                this.$toast.success('The card was successfully removed.')
            } catch (e) {
                let message = 'An error occured while trying to downgrade you. Please contact us and we will ensure your account is properly downgraded.'
                try {
                    message = e.response.body.error
                } catch (e) {}
                this.$toast.error(message)
            } finally {
                this.processing = false
            }
        },
        canRemoveCard (card) {
            if (!card.default) {
                return true
            }

            return !(this.account.plan && !this.account.subscription_ends_at)
        },
        showDowngradeModal () {
            this.$refs.downgrade.show()
        },
        async downgrade () {
            if (this.processing) {
                return
            }

            this.processing = true

            const renewDate = this.$options.filters.datetime(this.account.renew_date, 'dddd DD MMM')

            try {
                const response = await this.$http.post('account/downgrade', this.churn)
                this.$store.commit('set', response.body.account)
                this.$toast.success('You will be downgraded to the free plan at the end of your current subscription, on ' + renewDate + '.')
            } catch (e) {
                this.$toast.error('An error occured while trying to downgrade you. Please contact us to properly downgrade your account.')
            } finally {
                this.processing = false
                this.churn.reason = null
                this.churn.message = null
                this.$refs.downgrade.close()
            }
        },
        async enableSubscription () {
            if (this.processing) {
                return
            }

            this.processing = true

            try {
                const response = await this.$http.post('account/downgrade/cancel')
                this.$store.commit('set', response.body.account)
            } catch (e) {
                this.$toast.error('We could not re-enable your subscription.')
            } finally {
                this.processing = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
    @import '../../public/css/vars';

    a.disabled {
        opacity: .5;
        cursor: not-allowed;
        pointer-events: none;
    }

    .plan--current {
        padding: 40px;
        background: #fff url("/images/upgrade_rocket.svg") no-repeat top right;
        background-size: contain;

        .heading {
            display: flex;
            justify-content: flex-start;
            align-items: center;

            &>div, &>a {
                margin-left: 40px;
                &:first-child { margin-left: 0 }
            }

            &>a { display: block }
        }

        h3 {
            font-size: 2.8rem;
            margin: 0;
        }

        .price {
            span {
                font-size: 1.5em;
                font-weight: bold;
                vertical-align: super;
                color: $menu-active-color;
            }

            strong {
                font-size: 2.5em;
                color: $menu-active-color;
            }

            small {
                vertical-align: super;
                color: $link-color-secondary
            }
        }

        .downgrade {
            margin-top: 20px;

            &>a {
                border-color: $form-field-error-color;
                color: $form-field-error-color;

                &:hover { color: darken($form-field-error-color, 10%) }
            }
            &>p {
                font-style: italic;
                color: $form-field-error-color;
                time { border-bottom: dotted 1px $form-field-error-color }

                a.btn {
                    margin-left: 10px;
                    background-color: fade-out($button-light-text-color, .2);
                    border-color: $button-light-text-color;
                    color: #fff;
                    padding: 5px 10px;
                    border-radius: 3px;

                    &:hover {
                        background-color: $button-light-text-color;
                        box-shadow: none;
                    }
                }
            }
        }
    }

    .cards {
        margin-top: 60px;
        animation: 1s ease-in-out 0s 1 forwards fadeInFromNone;
    }

    .cards>.content {
        display: flex;
        justify-content: flex-start;
        flex-direction: row;
        flex-wrap: wrap;

        .card {
            flex: 0 0 25%;
            background-color: #fff;
            padding: 20px;
            border-radius: 12px;
            min-width: 250px;
            margin-left: 20px;
            margin-bottom: 20px;
            box-shadow: $box-shadow;
            background-color: #fcfffa;
            background-color: linear-gradient(45deg, rgba(252,256,250,1) 0%, #fff 100%);
            color: $text-color;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            .remove {
                transition: background-color $transition;
                background-color: rgba($form-field-error-bg-color, .5);
                padding: 10px 10px;
                border-radius: 50%;
                display: inline-block;

                svg {
                    width: 24px;
                    height: 24px;
                    stroke: $form-field-error-color;
                    vertical-align: sub;
                    opacity: 0.5;
                    transition: opacity $transition;
                }
            }

            &:hover .remove {
                &:not(.disabled) { opacity: 1 }
            }

            a {
                border: none;
                text-decoration: none;
                color: $text-color
            }

            &:nth-child(3n + 1) { margin-left: 0 }

            &.primary {
                background: rgb(108,32,205);
                background: linear-gradient(45deg, rgba(108,32,205,1) 0%, rgba(125,58,212,0.5046393557422969) 100%);
                color: #fff;

                a { color: #fff }

                .remove {
                    background-color: rgba(255, 255, 255, 0.5);
                    &:not(.disabled):hover {
                        background-color: #fff;
                        svg { opacity: 1 }
                    }
                }
            }

            .card--header, .card--details {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                margin-top: 20px;
                align-items: center;
            }

            .card--number {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                margin-top: 20px;
                font-family: Consolas, Menlo, Monaco, "Andale Mono WT", "Andale Mono", "Lucida Console", "Lucida Sans Typewriter", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Liberation Mono", "Nimbus Mono L", "Courier New", Courier, monospace;
            }

            .card--header { margin-top: 0 }

            .card--header img {
                height: 48px;
            }

            .card--details {
                .expires {
                    font-family: Consolas, Menlo, Monaco, "Andale Mono WT", "Andale Mono", "Lucida Console", "Lucida Sans Typewriter", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Liberation Mono", "Nimbus Mono L", "Courier New", Courier, monospace;
                }
            }

            &.card--add {
                padding: 0;
                border: solid 1px #ccc;

                a.add-action {
                    border-radius: 12px;
                    background-color: #eee;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    text-align: center;
                    justify-content: center;
                    padding: 0;

                    &:hover svg { stroke: #888 }
                }

                svg {
                    height: 128px;
                    stroke: #bbb;
                    stroke-width: 1px;
                    transition: stroke $transition;
                }
            }
        }
    }

    #cancel-survey {
        .cancel-survey-content {
            margin-top: 20px;

            .message {
                margin-bottom: 30px;

                .founder-message {
                    font-size: 1.05em;
                    line-height: 1.6;
                    color: #555;
                    margin: 0;
                }
            }

            .form-group {
                &:last-child {
                    margin-bottom: 25px;
                }

                .main-label {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 15px;
                    color: #333;
                    font-size: 1em;
                }

                label {
                    display: block;
                    margin-bottom: 8px;
                    color: #555;
                    font-size: 0.95em;
                }

                .reason-options {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;

                    .reason-option {
                        display: flex;
                        align-items: center;
                        padding: 12px 15px;
                        border: 2px solid #e0e0e0;
                        border-radius: 6px;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        background-color: #fafafa;

                        &:hover {
                            border-color: #7d3ad4;
                            background-color: #fff;
                        }

                        input[type="radio"] {
                            margin: 0 12px 0 0;
                            cursor: pointer;
                            width: 18px;
                            height: 18px;
                            flex-shrink: 0;
                        }

                        span {
                            font-size: 0.95em;
                            color: #333;
                            user-select: none;
                        }

                        &:has(input:checked) {
                            border-color: #7d3ad4;
                            background-color: #f8f4ff;
                        }
                    }
                }

                select {
                    color: $text-color
                }

                textarea {
                    width: 100%;
                    border: 2px solid #e0e0e0;
                    border-radius: 6px;
                    padding: 12px;
                    font-family: inherit;
                    font-size: 0.95em;
                    resize: vertical;
                    transition: border-color 0.2s ease;
                    color: #333;
                    margin-bottom: 0;

                    &:focus {
                        outline: none;
                        border-color: #7d3ad4;
                    }

                    &::placeholder {
                        color: #999;
                    }
                }

                .help-text {
                    margin-top: 0px;
                    font-size: 0.85em;
                    color: #666;
                    font-style: italic;
                    margin-bottom: 0;
                }
            }
        }
    }
</style>

<style>
#modal-stripe-card form>.main {
    min-height: 320px;
}
</style>
